import React, { CSSProperties, forwardRef, MouseEvent, useCallback } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Deal } from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import Chip from "@app/component/Chip";
import EntityBadge from "@app/component/EntityBadge";
import EntityChip from "@app/component/EntityChip";
import FieldGrid from "@app/component/FieldGrid";
import { filterPinnedFields } from "@app/component/FieldGrid/utils/util";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import RecordPreviewConfigurationColumns from "@app/store/recordPreview/RecordPreviewConfigurationColumns";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import { TimelineEventNewDeal } from "@app/types/events/TimelineEvent";

import BaseEvent from "./BaseEvent";
import styles from "./RecordEvent.module.scss";

export interface RecordEventProps {
  event: TimelineEventNewDeal;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  columns: RecordPreviewConfigurationColumns;
  onShowEntityView: (payload: EntityViewPayload) => void;
}

const NewDealEvent = forwardRef<HTMLDivElement, Props>(
  ({ columns, event, expanded, onShowEntityView, onSizeChange, style }, ref) => {
    const intl = useIntl();
    const record = event.entity;

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    return (
      <BaseEvent
        date={event.updatedAt}
        expanded={expanded}
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.newDeal",
            defaultMessage:
              "created {record} and its stage is {stage, select, false {unknown} other {{stage}}}",
            description: "Title of the New Deal event item",
          },
          {
            record: (
              <EntityChip
                entityId={record.id}
                entityType={record.entity}
                inline
                name={record.name}
                onClick={handleShowEntity}
              />
            ),
            stage: record.stage ? (
              <Chip inline>
                <EntityBadge entity={record.stage} />
              </Chip>
            ) : (
              false
            ),
          }
        )}
        user={event.updatedBy}
      >
        {columns[record.entity].filter(filterPinnedFields).length ? (
          <FieldGrid
            className={styles.grid}
            columns={columns[record.entity]}
            // TODO: we lack platform support here. We're intended to support full record body here, but only a few fields are available in reality
            entity={record as unknown as Deal}
            showEmpty
          />
        ) : null}
      </BaseEvent>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  columns: getRecordPreviewConfigurationColumns(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  NewDealEvent
);
